import React, { useState, useEffect, useRef } from "react";
import { PecIdentityFields } from "@/components/PecIdentityFields";
import { PecChargeSummary } from "@/components/PecChargeSummary";
import { AdminCost, useSourceCosts } from "@/components/AdminSourceCost";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { DocumentAutofill, mapDocToAnagrafica, mapVisuraToAnagrafica } from "@/components/DocumentAutofill";
import { Mail, CheckCircle2, XCircle, Download, Upload, FileText, Lock, Pencil, Check, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { previewPdf } from "@/lib/pdfPreview";
import { useAuth } from "@/context/AuthContext";
import { FieldError, errCls, focusFirstError } from "@/components/FieldError";

const EMPTY = { casella_pec: "", tipo_cliente: "privato", nome: "", cognome: "", codice_fiscale: "", email: "", telefono: "", data_nascita: "", sesso: "", nazione_nascita: "", comune_nascita: "", provincia_nascita: "", denominazione_titolare: "", cf_piva_titolare: "", indirizzo_titolare: "", comune_titolare: "", cap_titolare: "", nazione_titolare: "", provincia_titolare: "" };
const isoDate = (value) => typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value) ? value.split("/").reverse().join("-") : typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const STEP1 = ["nome", "cognome", "email", "telefono"];
const stepOf = (k) => (k === "casella" ? 0 : STEP1.includes(k) ? 1 : 2);
const telDigits = (v) => { const d = String(v || "").replace(/\D/g, ""); return d.startsWith("0039") ? d.slice(4) : d.startsWith("39") && d.length > 11 ? d.slice(2) : d; };
const pecErrors = (form, local) => {
  const e = {};
  const blank = (k) => !String(form[k] || "").trim();
  if (!local.trim()) e.casella = "Scrivi il nome della casella (la parte prima della @)";
  [...STEP1, "codice_fiscale", "data_nascita", "sesso", "nazione_nascita", "indirizzo_titolare", "cap_titolare", "comune_titolare", "nazione_titolare",
    ...(form.tipo_cliente === "azienda" ? ["denominazione_titolare", "cf_piva_titolare"] : []),
    ...(String(form.nazione_titolare || "").toUpperCase() === "IT" ? ["provincia_titolare"] : [])].forEach((k) => { if (blank(k)) e[k] = "Campo obbligatorio"; });
  if (!blank("email") && !EMAIL_RE.test(form.email.trim())) e.email = "Email non valida";
  if (!blank("telefono") && !/^\d{6,11}$/.test(telDigits(form.telefono))) e.telefono = "Numero non valido: da 6 a 11 cifre";
  if (!blank("codice_fiscale") && !/^[A-Z0-9]{16}$/i.test(form.codice_fiscale.trim())) e.codice_fiscale = "Il codice fiscale ha 16 caratteri";
  return e;
};

export default function Pec() {
  const { user } = useAuth();
  const draftKey = `pec-draft-${user?.id || "anon"}`;
  const draft = (() => { try { return JSON.parse(sessionStorage.getItem(draftKey) || "null"); } catch { return null; } })();
  const [form, setForm] = useState({ ...EMPTY, ...(draft?.form || {}) });
  const [step, setStep] = useState(draft?.step || 0);
  const submitting = useRef(false);
  const [activationError, setActivationError] = useState("");
  const [triedStep, setTriedStep] = useState(-1);
  const [blocked, setBlocked] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(draft?.customerId || "");
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const pickCustomer = (id) => {
    setCustomerId(id);
    const c = customers.find((x) => x.id === id); if (!c) return;
    const parts = (c.name || "").trim().split(/\s+/);
    const azienda = c.customer_type === "azienda";
    setForm((f) => ({ ...f,
      tipo_cliente: azienda ? "azienda" : "privato",
      nome: c.admin?.nome || (!azienda ? parts[0] : "") || "",
      cognome: c.admin?.cognome || (!azienda ? parts.slice(1).join(" ") : "") || "",
      codice_fiscale: c.admin?.codice_fiscale || (!azienda ? c.fiscal_code : "") || "",
      denominazione_titolare: azienda ? c.company_name || c.name || "" : "",
      cf_piva_titolare: azienda ? c.vat_number || c.fiscal_code || "" : "",
      data_nascita: isoDate(c.admin?.data_nascita || c.birth_date),
      comune_nascita: c.admin?.luogo_nascita || c.birth_city || "",
      sesso: "", nazione_nascita: "", provincia_nascita: "",
      indirizzo_titolare: c.address || "", comune_titolare: c.city || "", cap_titolare: c.zip || "",
      provincia_titolare: c.province || "", nazione_titolare: c.country || "",
      email: c.email || "",
      telefono: c.phone || "",
    }));
  };
  const [avail, setAvail] = useState(null); // null | true | false
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [retry, setRetry] = useState(0);
  const checkSeq = useRef(0);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [moduleBusy, setModuleBusy] = useState(false);
  const [moduleError, setModuleError] = useState("");
  const downloadModule = async () => {
    setModuleBusy(true); setModuleError("");
    try {
      const { data } = await api.get(`/openapi/pec/${encodeURIComponent(res.provider_id)}/activation-form`, { responseType: "blob" });
      previewPdf(data, "modulo-attivazione-pec.pdf");
    } catch { setModuleError("Modulo non disponibile. Riprova il download: la richiesta PEC è già registrata e non va ricreata."); }
    finally { setModuleBusy(false); }
  };
  const [kycDocs, setKycDocs] = useState([]); // [{name, b64}]
  const [kycBusy, setKycBusy] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [local, setLocal] = useState(draft?.local || "");
  const [domain, setDomain] = useState(draft?.domain || "legalmail.it");
  const [domains, setDomains] = useState([{ domain: "legalmail.it", label: "Legalmail" }]);
  const [svcPrice, setSvcPrice] = useState(null);
  const [balance, setBalance] = useState(null);
  const { costs: sourceCosts } = useSourceCosts();
  useEffect(() => { api.get("/wallet").then((r) => setBalance(r.data?.balance ?? null)).catch(() => {}); }, []);
  useEffect(() => { api.get("/openapi/service-prices").then((r) => setSvcPrice(r.data?.oa_pec_1y?.price ?? null)).catch(() => {}); }, []);
  const [requireDoc, setRequireDoc] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  const [locked, setLocked] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setRequireDoc(!!r.data.require_doc_pec)).catch(() => {}); }, []);
  const LOCKABLE = ["nome", "cognome", "codice_fiscale"];
  const lockOf = (k) => locked && LOCKABLE.includes(k) && !!(form[k] && String(form[k]).trim());
  const onDocData = (d, kind) => {
    if (kind === "visura") {
      const v = mapVisuraToAnagrafica(d);
      setForm((f) => ({ ...f, denominazione_titolare: v.ragione_sociale || f.denominazione_titolare, cf_piva_titolare: v.partita_iva || v.codice_fiscale || f.cf_piva_titolare, indirizzo_titolare: d.sede_indirizzo || f.indirizzo_titolare, comune_titolare: v.citta || f.comune_titolare, cap_titolare: v.cap || f.cap_titolare, provincia_titolare: v.provincia || f.provincia_titolare }));
    } else {
      const a = mapDocToAnagrafica(d);
      setForm((f) => ({ ...f, nome: a.nome || f.nome, cognome: a.cognome || f.cognome, codice_fiscale: a.codice_fiscale || f.codice_fiscale, data_nascita: isoDate(a.data_nascita) || f.data_nascita, comune_nascita: a.luogo_nascita || f.comune_nascita }));
    }
    setLocked(true);
  };
  useEffect(() => { api.get("/openapi/pec/domains").then((r) => { if (r.data.domains?.length) setDomains(r.data.domains); if (r.data.default && !draft?.domain) setDomain(r.data.default); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (res) { sessionStorage.removeItem(draftKey); return; }
    if (local || form.nome || form.cognome || form.codice_fiscale || customerId) sessionStorage.setItem(draftKey, JSON.stringify({ form, step, local, domain, customerId }));
  }, [form, step, local, domain, customerId, res, draftKey]);
  const clearDraft = () => { sessionStorage.removeItem(draftKey); setForm(EMPTY); setLocal(""); setCustomerId(""); setStep(0); setAvail(null); setLocked(false); setTriedStep(-1); };
  const fullPec = `${local.trim()}@${domain}`;
  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };
  const errs = pecErrors(form, local);
  const show = (k) => (errs[k] && triedStep >= stepOf(k) ? errs[k] : "");
  const next = () => {
    if (Object.keys(errs).some((k) => stepOf(k) === step)) { setTriedStep((t) => Math.max(t, step)); toast.error("Completa i campi evidenziati in rosso"); focusFirstError(); return; }
    if (step === 1 && requireDoc && !docUploaded) { toast.error("Carica il documento del cliente per procedere"); return; }
    setStep(step + 1);
  };

  const onKycFile = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name} supera 12 MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setKycDocs((prev) => [...prev.filter((x) => x.name !== f.name), { name: f.name, b64: reader.result }]);
      reader.readAsDataURL(f);
    });
  };

  const submitKyc = async () => {
    if (kycDocs.length < 1) { toast.error("Allega il documento d'identità e il modulo firmato"); return; }
    setKycBusy(true);
    try {
      const { data } = await api.post(`/openapi/pec/${res.provider_id}/kyc`, { documenti: kycDocs.map((d) => d.b64), confirm: true });
      setKycStatus(data.status || "activation_submitted");
      toast.success("Documenti inviati! Attivazione in corso.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio documenti fallito");
    } finally { setKycBusy(false); }
  };

  const check = () => setRetry((r) => r + 1);
  useEffect(() => {
    const id = ++checkSeq.current;
    setAvail(null); setCheckError(""); setChecking(false);
    const name = local.trim();
    if (name.length < 3 || !/^[a-z0-9][a-z0-9._-]*$/i.test(name)) { return undefined; }
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await api.get("/openapi/pec/check", { params: { address: fullPec } });
        if (id === checkSeq.current) { setAvail(data.available); }
      } catch (e) {
        if (id === checkSeq.current) { setCheckError(formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita"); }
      } finally { if (id === checkSeq.current) { setChecking(false); } }
    }, 700);
    return () => clearTimeout(timer);
  }, [fullPec, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAll = () => {
    const bad = Object.keys(errs);
    if (!bad.length) { setActivationError(""); return true; }
    setTriedStep(2); setActivationError("Completa i campi evidenziati in rosso prima di inviare.");
    const first = Math.min(...bad.map(stepOf)); if (first < step) setStep(first);
    focusFirstError(); return false;
  };

  const activate = async () => {
    if (submitting.current || blocked) return;
    if (requireDoc && !docUploaded) { toast.error("Carica il documento del cliente per procedere"); return; }
    if (!checkAll()) return;
    submitting.current = true; setBusy(true); setActivationError("");
    try {
      const { data } = await api.post("/openapi/pec/activate", { ...form, casella_pec: fullPec });
      setRes(data);
      if (data.balance != null) setBalance(data.balance);
      toast.success(`Richiesta PEC creata. Commissione ${euro(data.fee)}.`);
    } catch (e) {
      const message = formatApiErrorDetail(e.response?.data?.detail) || "Esito della richiesta non verificato. Controlla lo storico prima di ripetere.";
      setActivationError(message); toast.error(message);
      if (!e.response || e.response.status >= 500 || /non ripetere|esito.*verificare/i.test(message)) setBlocked(true);
    } finally { submitting.current = false; setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="pec-page">
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi pubblici</p>
        <ServiceHistoryButton service="pec" />
        </div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Mail className="w-7 h-7 text-primary" /> PEC — Posta Certificata</h1>
        <p className="text-sm text-muted-foreground mt-1">Verifica la disponibilità e attiva una casella PEC per il cliente.</p>
      </div>


      {!res && (
        <Card className="p-4 sm:p-5 glow-border space-y-3" data-testid="pec-form-card">
          <div className="flex flex-wrap items-center gap-1 pb-3 border-b border-border" data-testid="pec-steps">{["Casella", "Richiedente", "Titolare e conferma"].map((l, i) => <React.Fragment key={l}>{i > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}<button type="button" disabled={i > step} onClick={() => setStep(i)} data-testid={`pec-step-${i + 1}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${i === step ? "bg-primary text-primary-foreground font-semibold" : i < step ? "hover:bg-secondary" : "text-muted-foreground"}`}>{i < step ? <Check className="w-3.5 h-3.5" /> : <span className="font-bold">{i + 1}</span>}{l}</button></React.Fragment>)}{(local || form.nome || form.cognome || customerId) && <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 text-xs text-muted-foreground" onClick={clearDraft} data-testid="pec-clear-draft">Svuota modulo</Button>}</div>
          {step === 0 && <>
          <div>
            <Label>Cliente registrato (opzionale)</Label>
            <Select value={customerId} onValueChange={pickCustomer}>
              <SelectTrigger className="mt-1.5" data-testid="pec-customer-select"><SelectValue placeholder="Seleziona un cliente salvato o compila sotto" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`pec-customer-${c.id}`}>{c.name}{c.email ? ` — ${c.email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Casella PEC desiderata *</Label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-1.5">
              <Input className={`sm:!w-64 sm:!flex-none font-mono ${errCls(show("casella"))}`} aria-invalid={!!show("casella")} data-testid="pec-local-input" value={local}
                onChange={(e) => { setLocal(e.target.value.replace(/@.*$/, "").trim()); setAvail(null); }} placeholder="nome.cognome" />
              <Select value={domain} onValueChange={(v) => { setDomain(v); setAvail(null); }}>
                <SelectTrigger className="sm:w-auto gap-2 whitespace-nowrap font-mono" data-testid="pec-domain-select"><SelectValue>@{domain}</SelectValue></SelectTrigger>
                <SelectContent>
                  {domains.map((d) => <SelectItem key={d.domain} value={d.domain} className="whitespace-nowrap" data-testid={`pec-domain-${d.domain}`}>@{d.domain}{d.label ? ` · ${d.label}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FieldError msg={show("casella")} testid="pec-local-error" />
            <p className="text-xs text-muted-foreground mt-1.5 font-mono" data-testid="pec-full-address">{fullPec}</p>
          </div>
          <div className="min-h-5 text-sm" aria-live="polite" data-testid="pec-avail-status">
            {checking && <p className="text-orange-500 flex items-center gap-1.5" data-testid="pec-checking"><Loader2 className="w-4 h-4 animate-spin" /> Verifico la disponibilità…</p>}
            {!checking && avail === true && <p className="text-teal-500 flex items-center gap-1" data-testid="pec-avail"><CheckCircle2 className="w-4 h-4" /> Casella disponibile</p>}
            {!checking && avail === false && <p className="text-red-400 flex items-center gap-1" data-testid="pec-avail"><XCircle className="w-4 h-4" /> Casella già occupata: prova un altro nome</p>}
            {!checking && checkError && <p className="text-amber-500 flex flex-wrap items-center gap-1.5" data-testid="pec-check-error"><AlertTriangle className="w-4 h-4" />{checkError}<button type="button" className="underline" onClick={check} data-testid="pec-check-retry">Riprova</button></p>}
            {!checking && avail === null && !checkError && local.trim() && local.trim().length < 3 && <p className="text-xs text-muted-foreground" data-testid="pec-check-short">Scrivi almeno 3 caratteri: la verifica parte da sola.</p>}
          </div>

          <div className="rounded-lg border p-3 text-sm" data-testid="pec-mailbox-type">Casella Legalmail <strong>STANDARD</strong>. Il tipo di casella viene inviato automaticamente al fornitore; non è richiesto impostare una password in questo modulo.</div>
          </>}
          {step === 1 && <>
          <DocumentAutofill tipoCliente={form.tipo_cliente} required={requireDoc} onData={onDocData} onUploadedChange={setDocUploaded} />
          {locked && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid="pec-locked-banner">
              <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Dati anagrafici compilati dal documento e bloccati.</span>
              <Button type="button" variant="ghost" size="sm" className="gap-1 h-7" onClick={() => setLocked(false)} data-testid="pec-unlock-button"><Pencil className="w-3.5 h-3.5" /> Modifica</Button>
            </div>
          )}
          </>}
          {activationError && <p role="alert" className="text-sm text-destructive" data-testid="pec-activation-error">{activationError}</p>}
          {step === 1 && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2.5">
            <div>
              <Label>Tipo cliente</Label>
              <Select value={form.tipo_cliente} onValueChange={(v) => setForm({ ...form, tipo_cliente: v })}>
                <SelectTrigger className="mt-1.5" data-testid="pec-tipocliente"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="privato" data-testid="pec-type-privato">Privato</SelectItem>
                  <SelectItem value="azienda" data-testid="pec-type-azienda">Azienda</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="flex items-center gap-1">Nome *{lockOf("nome") && <Lock className="w-3 h-3 text-muted-foreground" />}</Label><Input className={`mt-1.5 ${errCls(show("nome"))}`} aria-invalid={!!show("nome")} data-testid="pec-nome-input" value={form.nome} onChange={set("nome")} disabled={lockOf("nome")} /><FieldError msg={show("nome")} testid="pec-nome-error" /></div>
            <div><Label className="flex items-center gap-1">Cognome *{lockOf("cognome") && <Lock className="w-3 h-3 text-muted-foreground" />}</Label><Input className={`mt-1.5 ${errCls(show("cognome"))}`} aria-invalid={!!show("cognome")} data-testid="pec-cognome-input" value={form.cognome} onChange={set("cognome")} disabled={lockOf("cognome")} /><FieldError msg={show("cognome")} testid="pec-cognome-error" /></div>
            <div><Label>Email di contatto *</Label><Input type="email" className={`mt-1.5 ${errCls(show("email"))}`} aria-invalid={!!show("email")} data-testid="pec-email-input" value={form.email} onChange={set("email")} /><FieldError msg={show("email")} testid="pec-email-error" /></div>
            <div><Label>Telefono (solo cifre, massimo 11) *</Label><Input maxLength={11} type="tel" className={`mt-1.5 ${errCls(show("telefono"))}`} aria-invalid={!!show("telefono")} data-testid="pec-telefono-input" value={form.telefono} onChange={set("telefono")} /><FieldError msg={show("telefono")} testid="pec-telefono-error" /></div>
          </div>}
          {step < 2 && <div className="flex items-center gap-2 pt-1">
            {step > 0 && <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} data-testid="pec-back">Indietro</Button>}
            <Button type="button" className="ml-auto gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pec-next" disabled={step === 0 && avail === false} onClick={next}>Avanti <ChevronRight className="w-4 h-4" /></Button>
          </div>}
          {step === 2 && <>
          <p className="text-xs text-muted-foreground font-mono" data-testid="pec-step3-recap">{fullPec} · {form.nome} {form.cognome}</p>
          <PecIdentityFields form={form} onChange={set} cfLocked={lockOf("codice_fiscale")} errors={Object.fromEntries(Object.keys(errs).map((k) => [k, show(k)]))} />
          {svcPrice != null && <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="pec-price">Costo del servizio: <strong>{euro(svcPrice)}</strong> · <span className="text-muted-foreground">valida 12 mesi, rinnovo annuale ricomprando il servizio</span> · verrà addebitato dal tuo Plafond servizi alla conferma di registrazione della richiesta da parte del fornitore. <AdminCost cost={sourceCosts?.openapi?.oa_pec_1y} testId="pec-source-cost" /></div>}
          <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="ghost" onClick={() => setStep(1)} data-testid="pec-back-confirm">Indietro</Button>
          <ConfirmButton onConfirm={activate} beforeOpen={checkAll} disabled={busy || blocked || (requireDoc && !docUploaded)} className="gap-2" data-testid="pec-activate-button"
            title="Riepilogo attivazione PEC"
            description={<PecChargeSummary address={fullPec} fee={svcPrice} balance={balance} />}
            confirmText="Attiva PEC">
            <Mail className="w-4 h-4" /> {busy ? "Invio…" : (svcPrice != null ? `Attiva PEC · ${euro(svcPrice)}` : "Attiva PEC")}
          </ConfirmButton>
          </div>
          <p className="text-xs text-muted-foreground">Dopo la creazione sarà necessario completare l'attivazione (documento d'identità del titolare) tramite il modulo fornito.</p>
          </>}
        </Card>
      )}

      {res && (
        <Card className="p-6 glow-border space-y-4" data-testid="pec-result-card">
          <div className="flex items-center gap-2"><h2 className="font-head text-lg font-semibold">Richiesta PEC creata</h2><Badge className="bg-amber-500 ml-auto">In attivazione</Badge></div>
          <p className="text-sm text-muted-foreground">ID pratica: <span className="font-mono">{res.provider_id}</span></p>
          {res.modulo_attivazione && (
            <Button variant="outline" disabled={moduleBusy} onClick={downloadModule} className="gap-2" data-testid="pec-modulo-link"><Download className="w-4 h-4" />{moduleBusy ? "Recupero modulo…" : "Scarica il modulo di attivazione"}</Button>
          )}

          {moduleError && <p role="alert" className="text-sm text-destructive" data-testid="pec-module-error">{moduleError}</p>}
          {kycStatus ? (
            <div className="rounded-lg border border-teal-500/50 bg-teal-500/5 p-4" data-testid="pec-kyc-done">
              <p className="text-sm text-teal-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Documenti inviati. Stato pratica: <strong className="capitalize">{kycStatus}</strong></p>
              <p className="text-xs text-muted-foreground mt-1">L'attivazione verrà completata dal provider. Potrai verificarne lo stato in seguito.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border p-4 space-y-3" data-testid="pec-kyc-card">
              <p className="text-sm font-medium flex items-center gap-2"><Upload className="w-4 h-4 text-primary" /> Completa l'attivazione (KYC)</p>
              <p className="text-xs text-muted-foreground">Allega il <strong>documento d'identità</strong> del titolare e il <strong>modulo di attivazione firmato</strong> (PDF). Verranno inviati al provider per completare l'attivazione.</p>
              <Input type="file" accept=".pdf,image/*" multiple data-testid="pec-kyc-file" onChange={onKycFile} />
              {kycDocs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {kycDocs.map((d) => <Badge key={d.name} variant="outline" className="gap-1" data-testid={`pec-kyc-doc-${d.name}`}><FileText className="w-3 h-3" /> {d.name}</Badge>)}
                </div>
              )}
              <Button onClick={submitKyc} disabled={kycBusy || kycDocs.length < 1} className="gap-2" data-testid="pec-kyc-submit">
                <Upload className="w-4 h-4" /> {kycBusy ? "Invio documenti…" : "Invia documenti e attiva"}
              </Button>
              <p className="text-[11px] text-muted-foreground">Azione definitiva: l'invio avvia l'attivazione reale della PEC.</p>
            </div>
          )}

          {kycStatus && <Button variant="ghost" onClick={() => { setRes(null); setForm(EMPTY); setAvail(null); setKycDocs([]); setKycStatus(null); setActivationError(""); setBlocked(false); setModuleError(""); setLocal(""); setStep(0); }} data-testid="pec-new">Nuova PEC</Button>}
        </Card>
      )}
    </div>
  );
}
